import { SiteHeader } from "@/components/site-header"
import { Link } from "react-router-dom"
import { ArrowLeftIcon } from "lucide-react"

/**
 * /docs/contratos
 *
 * Documents the contracts (relations) module:
 *   - Data model: contracts → contract_weeks → contract_monthly_targets
 *   - Month generation and week distribution logic
 *   - Monthly target override (contract_monthly_targets)
 *   - Week stats computation (real TN, completion %, trucks needed)
 *   - Shipment cancellation exclusion from stats
 *   - Permissions: admin + operator only
 */
export default function ContractsGuidePage() {
  return (
    <>
      <SiteHeader title="Contratos de Transporte" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="space-y-8 max-w-3xl">

          <div>
            <Link
              to="/docs"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeftIcon className="size-3" /> Documentación
            </Link>
            <h2 className="text-base font-semibold mb-1">Contratos de Transporte</h2>
            <p className="text-sm text-muted-foreground">
              El módulo de <strong>Contratos</strong> permite hacer seguimiento mensual del tonelaje real
              transportado por línea de trabajo y cliente, comparándolo contra objetivos semanales y
              mensuales. Accesible desde <strong>Relaciones → Contratos</strong> (admin y operator).
            </p>
          </div>

          {/* ─── Data model ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Modelo de datos</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">contracts</p>
                <p>Contrato principal. Campos clave:</p>
                <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                  <li><code className="font-mono">client_id</code> — FK al cliente.</li>
                  <li><code className="font-mono">work_line</code> — <code className="font-mono">batea | nacional | internacional</code>.</li>
                  <li><code className="font-mono">monthly_target_tn</code> — objetivo mensual por defecto (usado cuando no hay override).</li>
                  <li><code className="font-mono">ref_weight_tn_per_truck</code> — tonelaje de referencia por camión para calcular camiones necesarios.</li>
                  <li><code className="font-mono">start_date / end_date</code> — vigencia del contrato.</li>
                </ul>
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">contract_weeks</p>
                <p>Semanas del contrato dentro de un mes. Generadas automáticamente via{" "}
                  <code className="font-mono text-xs">POST /api/relations/contracts/:id/weeks/generate</code>.
                </p>
                <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                  <li>Cada semana corre de lunes a domingo; la última semana del mes se recorta al último día del mes.</li>
                  <li><code className="font-mono">target_tn</code> — objetivo de esa semana (distribuido proporcionalmente del objetivo mensual).</li>
                  <li>Semanas existentes no se sobreescriben (idempotente).</li>
                </ul>
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">contract_monthly_targets</p>
                <p>Override opcional del objetivo mensual por contrato y mes. Si existe una fila para
                  <code className="font-mono text-xs"> (contract_id, year_month)</code>, ese valor
                  reemplaza al <code className="font-mono text-xs">monthly_target_tn</code> del contrato
                  para ese mes específico.</p>
                <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                  <li><code className="font-mono">year_month</code> — formato <code className="font-mono">YYYY-MM</code> (ej: <code className="font-mono">2025-06</code>).</li>
                  <li>Upsert via <code className="font-mono text-xs">PUT /api/relations/contracts/:id/monthly-targets/:yearMonth</code>.</li>
                  <li>La API también lo lee en <code className="font-mono text-xs">GET /monthly-targets/:yearMonth</code> para mostrar el valor actual en el diálogo.</li>
                  <li>La respuesta del endpoint de estadísticas incluye <code className="font-mono">hasCustomTarget: boolean</code> para que la UI indique visualmente si hay override.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ─── Week generation ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Generación de semanas</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">
                  <code className="font-mono text-xs">POST /api/relations/contracts/:id/weeks/generate</code>
                </p>
                <p>Requiere <code className="font-mono text-xs">?year=YYYY&month=M</code> en la query string.</p>
                <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                  <li>Avanza al primer lunes del mes.</li>
                  <li>Genera rangos lunes–domingo hasta fin de mes; el último rango termina el último día del mes (no el domingo siguiente).</li>
                  <li>Distribuye el objetivo mensual efectivo (<code className="font-mono">monthly_target_tn</code> o override) de forma proporcional entre todas las semanas del mes.</li>
                  <li>Devuelve <code className="font-mono">{"{ created, skipped }"}</code>. Llamadas repetidas para el mismo mes solo crean las semanas faltantes.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ─── Stats computation ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Cálculo de estadísticas mensuales</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                El endpoint <code className="font-mono text-xs">GET /api/relations/contracts/:id/monthly-stats?year=&month=</code> devuelve
                las semanas del mes con sus KPIs calculados en tiempo real, más los totales mensuales.
              </p>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">Por semana</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li><strong>realTn</strong> — suma de <code className="font-mono">unloading_weight_kg / 1000</code> de los envíos del cliente y línea de trabajo cuyo <code className="font-mono">origin_loading_at</code> cae dentro de la semana.</li>
                  <li><strong>completionPct</strong> — <code className="font-mono">realTn / targetTn × 100</code>.</li>
                  <li><strong>truckNeeded</strong> — <code className="font-mono">⌈targetTn / refWeightTnPerTruck⌉</code>.</li>
                  <li><strong>truckCount</strong> — número de envíos con carga en la semana (proxy de camiones).</li>
                  <li><strong>truckMissing</strong> — <code className="font-mono">max(0, truckNeeded − truckCount)</code>.</li>
                </ul>
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">Envíos excluidos de estadísticas</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Los envíos con <code className="font-mono">status = 'cancelado'</code> no se incluyen en el cálculo de <code className="font-mono">realTn</code>.</li>
                  <li>Los envíos con <code className="font-mono">deleted_at IS NOT NULL</code> tampoco se incluyen.</li>
                </ul>
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1.5">
                <p className="font-medium text-foreground">Totales mensuales</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li><strong>monthlyTargetTn</strong> — override de <code className="font-mono">contract_monthly_targets</code> si existe, o <code className="font-mono">monthly_target_tn</code> del contrato.</li>
                  <li><strong>totalRealTn</strong> — suma de <code className="font-mono">realTn</code> de todas las semanas.</li>
                  <li><strong>completionPct</strong> — <code className="font-mono">totalRealTn / monthlyTargetTn × 100</code>.</li>
                  <li><strong>remainingTn</strong> — <code className="font-mono">max(0, monthlyTargetTn − totalRealTn)</code>.</li>
                  <li><strong>remainingTrucks</strong> — estimación de camiones para cubrir el remanente.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ─── Monthly target override ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Override de objetivo mensual</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Desde la UI del detalle del contrato, el botón <strong>"Objetivo mensual"</strong> abre un diálogo
                que permite definir un objetivo específico para el mes en curso. Al guardar se hace un upsert
                en <code className="font-mono text-xs">contract_monthly_targets</code>.
              </p>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1">
                <p className="font-medium text-foreground">Lógica de prioridad</p>
                <ol className="list-decimal list-inside text-xs space-y-1 mt-1">
                  <li>Si existe fila en <code className="font-mono">contract_monthly_targets</code> para <code className="font-mono">(contract_id, year_month)</code> → ese valor es el objetivo efectivo.</li>
                  <li>Si no existe → se usa <code className="font-mono">contracts.monthly_target_tn</code> como fallback.</li>
                </ol>
                <p className="text-xs mt-2">
                  La misma lógica se aplica al generar semanas: si hay un override, las semanas generadas
                  se distribuyen sobre el override, no sobre el valor del contrato.
                </p>
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1">
                <p className="font-medium text-foreground">hasCustomTarget</p>
                <p className="text-xs">
                  El campo <code className="font-mono">hasCustomTarget: boolean</code> en la respuesta de estadísticas
                  indica si el objetivo del mes viene de un override o del contrato. La UI lo usa para
                  mostrar un badge <code className="font-mono">objetivo personalizado</code> junto al valor.
                </p>
              </div>
            </div>
          </section>

          {/* ─── Permissions ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Permisos</h3>
            <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm text-muted-foreground space-y-1">
              <p>El recurso <code className="font-mono">contracts</code> en <code className="font-mono">permissions.ts</code>:</p>
              <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                <li><strong>admin</strong> — <code className="font-mono">ALL</code> (visit, write, delete, restore).</li>
                <li><strong>operator</strong> — <code className="font-mono">ALL</code>.</li>
                <li><strong>fleet_manager / driver</strong> — <code className="font-mono">NONE</code>.</li>
              </ul>
              <p className="text-xs mt-2">
                La ruta <code className="font-mono">/relations/contracts</code> usa{" "}
                <code className="font-mono">R_CONTRACTS = rolesWithPermission("contracts", "visit")</code> en <code className="font-mono">App.tsx</code>.
                Los botones de escritura usan <code className="font-mono">can("write", "contracts")</code>.
              </p>
            </div>
          </section>

          {/* ─── API endpoints ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-1">Endpoints</h3>
            <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left pb-1 pr-4 font-medium text-foreground">Método</th>
                    <th className="text-left pb-1 pr-4 font-medium text-foreground">Ruta</th>
                    <th className="text-left pb-1 font-medium text-foreground">Descripción</th>
                  </tr>
                </thead>
                <tbody className="space-y-1">
                  {[
                    ["GET",    "/api/relations/contracts",                                  "Lista de contratos (paginada, búsqueda)."],
                    ["POST",   "/api/relations/contracts",                                  "Crear contrato."],
                    ["GET",    "/api/relations/contracts/:id",                              "Detalle del contrato."],
                    ["PATCH",  "/api/relations/contracts/:id",                              "Actualizar contrato."],
                    ["DELETE", "/api/relations/contracts/:id",                              "Soft delete."],
                    ["PATCH",  "/api/relations/contracts/:id/restore",                      "Restaurar."],
                    ["GET",    "/api/relations/contracts/:id/monthly-stats?year=&month=",   "Estadísticas mensuales con semanas y KPIs."],
                    ["POST",   "/api/relations/contracts/:id/weeks/generate?year=&month=",  "Generar semanas del mes."],
                    ["POST",   "/api/relations/contracts/:id/weeks",                        "Crear semana manual."],
                    ["PATCH",  "/api/relations/contracts/:id/weeks/:weekId",                "Editar semana."],
                    ["DELETE", "/api/relations/contracts/:id/weeks/:weekId",                "Eliminar semana."],
                    ["GET",    "/api/relations/contracts/:id/monthly-targets/:yearMonth",    "Leer override de objetivo mensual."],
                    ["PUT",    "/api/relations/contracts/:id/monthly-targets/:yearMonth",    "Upsert override de objetivo mensual."],
                  ].map(([method, path, desc]) => (
                    <tr key={path} className="border-b last:border-0">
                      <td className="py-1.5 pr-4 font-mono font-medium">{method}</td>
                      <td className="py-1.5 pr-4 font-mono">{path}</td>
                      <td className="py-1.5">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
